import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';

import { AriaWidgetRole } from '../../../constants/aria-widget-roles';
import { noop } from '../../default-props/noop';
import { Button } from '../button';

// @ts-expect-error less import
import style from './dropdown-menu-item.module.less';
import { DropdownMenuType } from '../dropdown-menu/dropdown-menu-type';
// @ts-expect-error less import
import dropdownMenuStyle from '../dropdown-menu/dropdown-menu.module.less';

/**
 * @typedef {import('./dropdown-menu-item-types.d.ts').DropdownMenuItemComponent} DropdownMenuItemComponent
 */

/**
 * @param {string|undefined} dropdownMenuType
 * @returns {string|undefined}
 */
const getRole = (dropdownMenuType) => {
  switch (dropdownMenuType) {
    case DropdownMenuType.ComboBox:
      return AriaWidgetRole.Option;
    case DropdownMenuType.Menu:
      return AriaWidgetRole.MenuItem;
    default:
      return undefined;
  }
};

/**
 * @type {DropdownMenuItemComponent}
 */
export const DropdownMenuItem = ({
  children,
  className = '',
  disabled = false,
  disabledTooltipMessage,
  disabledTooltipMessagePosition,
  disabledTooltipWrapperClassName,
  dropdownMenuType,
  hovered = false,
  href = '',
  id,
  index,
  name,
  onClick = noop,
  onMouseEnter = noop,
  onRowHover = noop,
  pendoTrackingId,
  rel = 'noreferrer noopener',
  removePadding = false,
  selected = false,
  target = '_blank',
  testId,
  theme = 'unstyled',
  title,
  useAdvancedTruncationTitleLogic,
}) => {
  /**
   * @type {React.RefObject<HTMLButtonElement | HTMLAnchorElement>}
   */
  const ref = useRef(null);

  const onButtonClick = useCallback(
    (...args) => {
      if (name || (typeof name === 'string' && name.length >= 0)) {
        return onClick(name);
      }
      onClick(...args);
    },
    [name, onClick],
  );

  useLayoutEffect(() => {
    if (ref.current && hovered) {
      /** @type {HTMLElement | null} */
      const dropdown = ref.current.closest(`.${dropdownMenuStyle['inner-dropdown-menu']}`);
      if (!dropdown) {
        return;
      }

      const { offsetHeight: dropdownHeight, scrollTop } = dropdown;
      const { offsetHeight, offsetTop } = ref.current;

      if (offsetTop < scrollTop) {
        ref.current.scrollIntoView({ block: 'start' });
      } else if (offsetTop + offsetHeight > scrollTop + dropdownHeight) {
        ref.current.scrollIntoView({ block: 'end' });
      }
    }
  }, [hovered]);

  const otherProperties = useMemo(() => {
    return {
      role: getRole(dropdownMenuType),
      'aria-selected': selected,
    };
  }, [dropdownMenuType, selected]);

  const classes = classnames(className, style['dropdown-menu-item'], {
    [style['remove-padding']]: removePadding,
    [style.disabled]: disabled,
    hover: hovered,
    [style.hover]: hovered,
    selected,
    [style.selected]: selected,
  });

  // Some of our consumers render buttons themselves, which lead to invalid nesting
  // errors within React. Solutions would be to refactor those consumers to remove
  // the nested buttons, or for them to embed divs in place of DropdownMenuItem
  // components, but then that causes warnings for prop-type validation, because
  // we have a custom validation function that checks for only DropdownMenuToggle
  // and DropdownMenuItem children. By allowing a function 'children' prop in this
  // component, we can let them consume DropdownMenuItem properly for validation
  // purposes, while still rendering a div wrapper instead of a button wrapper.
  if (typeof children === 'function') {
    return children({ className: classes, ref });
  }

  return (
    <Button
      ref={ref}
      buttonTitle={title}
      className={classes}
      disabled={disabled}
      disabledTooltipMessage={disabledTooltipMessage}
      disabledTooltipMessagePosition={disabledTooltipMessagePosition}
      disabledTooltipWrapperClassName={classnames(style['dropdown-menu-item-disabled-tooltip-wrapper'], disabledTooltipWrapperClassName)}
      href={href}
      id={id}
      onClick={onButtonClick}
      onMouseEnter={() => {
        onRowHover(index);
        onMouseEnter(index);
      }}
      onMouseLeave={() => onRowHover(undefined)}
      otherProperties={otherProperties}
      pendoTrackingId={pendoTrackingId}
      rel={rel}
      target={target}
      testId={testId}
      theme={theme}
      useAdvancedTruncationTitleLogic={useAdvancedTruncationTitleLogic}
    >
      {children}
    </Button>
  );
};
